import { useEffect } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { AppShell } from "../components/layout/app-shell"
import { QueryState } from "../components/layout/query-state"
import { byMarketplaceRevenue } from "../lib/marketplace-revenue-order"
import { useMarketplaceSummaries } from "../services/use-dashboard"
import { useRates } from "../services/use-rates"

/**
 * Entry point for the profit & loss statement: a pass-through to one
 * marketplace's page, never a page of its own — the same shape the keyword
 * tracker uses, and for the same reason. A chooser page would cost two clicks to
 * reach any statement, and switching belongs on the statement itself.
 *
 * Lands on the BIGGEST channel by revenue, the same order the switcher lists
 * (`byMarketplaceRevenue`). Unlike the keyword tracker there is no `primary`
 * flag to honour here — every configured marketplace has a statement — so size
 * stands in for it rather than the API's arbitrary list position.
 */
function PnlIndexPage() {
  const summaries = useMarketplaceSummaries()
  const rates = useRates()
  const navigate = useNavigate()
  // Rates decide the order, so the landing choice must wait for them: redirecting
  // first and resorting later would drop the operator on the wrong statement.
  const ranked = rates.isLoading
    ? []
    : [...(summaries.data ?? [])].sort(byMarketplaceRevenue(rates.data))
  const landing = ranked[0]?.marketplace

  useEffect(() => {
    if (landing) {
      void navigate({ to: "/pnl/$marketplace", params: { marketplace: landing }, replace: true })
    }
  }, [landing, navigate])

  return (
    <AppShell title="Profit & loss" description="Whole-marketplace revenue against every cost.">
      <QueryState
        // Redirecting counts as still loading: rendering an empty shell in the
        // frame before the navigation lands reads as "no data".
        isLoading={summaries.isLoading || rates.isLoading || landing !== undefined}
        error={summaries.error}
        isEmpty={summaries.data?.length === 0}
        emptyMessage="No marketplace is configured yet."
      >
        {null}
      </QueryState>
    </AppShell>
  )
}

export const Route = createFileRoute("/pnl/")({ component: PnlIndexPage })
